/** @file PoseSection: the selected node's pose at the playhead: rotation, position, scale, source, buttons. */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import type { ReactElement } from 'react';
import { InfoTip } from '../../ui-kit/InfoTip';
import { useEditor } from '../../state/useEditor';
import { AngleField } from './AngleField';
import { InspectorSection } from './InspectorSection';
import { nodeTransform } from './nodeTransform';
import { numericRules } from './numericRules';
import { PoseButtons } from './PoseButtons';
import { poseSource } from './poseSource';
import { transformReason } from './transformReason';
import { useJointEdit, type JointEdit } from './useJointEdit';
import { VecInput } from './VecInput';

const AXES = ['X', 'Y', 'Z'] as const;
/**
 * Rotation rows in Unity order X / Y / Z; up is +Z. The key stores [yaw, pitch, roll]: X = pitch (index 1),
 * Y = roll (index 2), Z = yaw (index 0, the turn that picks the facing tile). The title names the angle.
 */
const ANGLES = [['X', 'pitch: nod about X', 1], ['Y', 'roll: tilt about Y', 2], ['Z', 'yaw: turn about the up axis (picks the facing tile)', 0]] as const;
const TITLES = {
  pos: ['X (east)', 'Y (north)', 'Z (up)'],
  scale: ['X (uniform: a key holds one scale)', 'Y (follows X)', 'Z (follows X)'],
};
const UNIFORM = 'Scale is uniform (a key holds one scale): edit X, Y and Z follow.';
const UNIFORM_AXES = [false, true, true] as const;
const MUTED = 'var(--p3-muted, #8b919e)';
const BADGE = { fontSize: 10, padding: '0 6px', borderRadius: 8, border: '1px solid var(--p3-border, #2a2e38)', color: MUTED };

/** Display rounding (the stored value is never rounded). */
const round = (n: number): number => Math.round(n * 1e4) / 1e4;

/** The title's right-hand note: `<clip> @ <frame>`, `rest` without a clip, or the mirrored half. */
function asideOf(edit: JointEdit | null): string {
  if (edit === null) return 'rest';
  return edit.locked ? 'mirrored half (computed)' : `${edit.clipName} @ ${String(edit.frame)}`;
}

/**
 * One block for the node's key at the playhead (it replaces the Joint and Transform blocks): Rotation X / Y / Z
 * (pitch, roll, yaw) as a number field and a slider per row (`AngleField`: drags and scrubs preview through `poseOverride`, release
 * or typing keys once), Position = the node's `pos` plus the key translation, the uniform key Scale, the source badge
 * and Reset to rest / Copy to twin. Without a clip it shows the document pos and rest rotation read-only.
 */
export function PoseSection({ node }: { node: string }): ReactElement | null {
  const puppetNode = useEditor((s) => s.puppet.doc?.puppet.nodes[node]);
  const edit = useJointEdit(node);
  if (puppetNode === undefined) return null;
  const t = nodeTransform(puppetNode.pos, puppetNode.rest, edit, node);
  const disabled = edit === null || edit.locked;
  const reason = transformReason(edit);
  const write = (fields: NodeKey): void => { edit?.preview(fields); edit?.commit(); };
  const keyPosOf = (axis: number, v: number): [number, number, number] => {
    const keyPos: [number, number, number] = [...t.keyPos];
    keyPos[axis] = v - (puppetNode.pos[axis] ?? 0);
    return keyPos;
  };
  const cancel = (): void => { edit?.cancel(); };
  return (
    <InspectorSection title="Pose" aside={asideOf(edit)} extra={<InfoTip label="Number field rules" lines={numericRules} />}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: MUTED }}>Rotation (deg)</span>
        <span data-badge="pose" style={BADGE}>{poseSource(edit)}</span>
      </div>
      {ANGLES.map(([label, title, axis]) => (
        <AngleField key={label} label={label} title={title} value={round(t.rot[axis])} muted={!t.rotKeyed} disabled={disabled}
          onChange={(deg, final) => { edit?.setRot(axis, deg, final); }} onCancel={cancel} />
      ))}
      {reason !== null && <div data-reason="Rotation" style={{ fontSize: 10, color: MUTED }}>{reason}</div>}
      <VecInput label="Position" unit="unit" axes={AXES} titles={TITLES.pos} value={t.pos.map(round)} step={0.01} disabled={disabled} reason={reason}
        muted={!t.posKeyed} onCommit={(next, axis) => { write({ pos: keyPosOf(axis, next[axis] ?? 0) }); }}
        onPreview={(next, axis) => { edit?.preview({ pos: keyPosOf(axis, next[axis] ?? 0) }); }} onCancel={cancel} />
      <VecInput label="Scale" unit="x" axes={AXES} titles={TITLES.scale} value={[t.scale, t.scale, t.scale]} step={0.01} disabled={disabled}
        disabledAxes={UNIFORM_AXES} reason={reason ?? UNIFORM} muted={!t.scaleKeyed} onCommit={(next, axis) => { write({ scale: next[axis] ?? 1 }); }}
        onPreview={(next, axis) => { edit?.preview({ scale: next[axis] ?? 1 }); }} onCancel={cancel} />
      {edit !== null && <PoseButtons edit={edit} node={node} />}
    </InspectorSection>
  );
}
